import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, InputBase, Avatar, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import baseurl from '../Api/BaseUrl';
import { ShowClientsActionsAdvanced } from '../Pages/ShowClientsActionsAdvanced';
import { createPortal } from 'react-dom';

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg:                '#FFFFFF',
  border:            '#EAECF0',
  text:              '#101828',
  textMuted:         '#667085',
  accent:            '#1A3ECF',
  accentSoft:        '#EEF1FD',
  searchBorderFocus: '#1A3ECF',
  shadow:            '0 8px 40px rgba(16,24,40,0.13)',
  rowHover:          '#F5F7FF',
};

const SEARCH_CONFIG = {
  Admin:         { placeholder: 'Search by name, phone, email... then press Enter', storageKey: 'mainsearchstorage',             event: 'mainsearchupdated' },
  'Team Leader': { placeholder: 'Search leads by name, email or phone...',           storageKey: 'searchsalesstorage',            event: 'searchsalesUpdated' },
  Manager:       { placeholder: 'Search leads by name, email or phone...',           storageKey: 'searchsalesstoragewithmanager', event: 'searchsalesmanagerUpdated' },
  Sales:         { placeholder: 'Search leads by name, email or phone...',           storageKey: 'searchsalesuserstorage',        event: 'searchsalesuserstorageUpdate' },
  Accountant:    { placeholder: 'Search done deals by name, email or phone...',      storageKey: 'searchAccountantuserstorage',   event: 'searchAccountantuserstorageUpdate' },
  Marketer:      { placeholder: 'Search leads by name, email or phone...',           storageKey: 'searchMarketerstorage',         event: 'searchMarketerUpdated' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getInitials = (name = '') =>
  (name || 'U').split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2);

const avatarColor = (name = '') => {
  const palette = ['#0D47A1','#1565C0','#1976D2','#16A34A','#D97706','#7C3AED','#0891B2','#DB2777'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % palette.length;
  return palette[h];
};

// ── Lead location detector ───────────────────────────────────────────────────
const getLeadLocation = (lead) => {
  const isActive    = lead?.leadisactive === 'false' || lead?.leadisactive === false;
  const transferred = lead?.transferefromdata === true;

  // Trash — leadisactive = true بس
  if (isActive) return {
    label: 'Trash',
    bg:    '#FEE2E2',
    color: '#DC2626',
    icon: (
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
        <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2"/>
      </svg>
    ),
  };

  // Data Center — transferefromdata = false
  if (!transferred) return {
    label: 'Data Center',
    bg:    '#FEF3C7',
    color: '#D97706',
    icon: (
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
        <ellipse cx="12" cy="5" rx="9" ry="3"/>
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
      </svg>
    ),
  };

  // Original Leads — transferefromdata = true
  return {
    label: 'Original Leads',
    bg:    '#DCFCE7',
    color: '#16A34A',
    icon: (
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
  };
};

const hl = (text = '', query = '') => {
  if (!query.trim() || !text) return text;
  const safe  = query.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${safe})`, 'gi');
  const parts = String(text).split(regex);
  return parts.map((part, i) =>
    regex.test(part)
      ? <mark key={i} style={{ background:'#DBEAFE', color:'#1A3ECF', fontWeight:700, borderRadius:'3px', padding:'0 2px' }}>{part}</mark>
      : part
  );
};

// ─── Result row ───────────────────────────────────────────────────────────────
const ResultRow = ({ lead, query, onSelect, index }) => {
  const bg = avatarColor(lead.name || '');
  return (
    <Box
      onClick={() => onSelect(lead)}
      sx={{
        display:'flex', alignItems:'center', gap:'14px',
        px:'16px', py:'12px',
        cursor:'pointer',
        borderBottom:`1px solid ${C.border}`,
        transition:'background 0.13s',
        animation:'srFadeIn 0.18s ease both',
        animationDelay:`${index * 40}ms`,
        '&:hover':{ background:C.rowHover },
        '&:last-child':{ borderBottom:'none' },
        '@keyframes srFadeIn':{
          from:{ opacity:0, transform:'translateY(5px)' },
          to:  { opacity:1, transform:'none' },
        },
      }}
    >
      {/* Avatar */}
      <Avatar sx={{
        width:42, height:42, flexShrink:0,
        fontSize:'13px', fontWeight:700,
        fontFamily:"'DM Sans', sans-serif",
        bgcolor:bg, color:'#fff',
        borderRadius:'11px',
        boxShadow:`0 2px 8px ${bg}55`,
      }}>
        {getInitials(lead.name)}
      </Avatar>

      {/* Info */}
      <Box sx={{ flex:1, minWidth:0 }}>
        {/* Row 1 — name + pills */}
        <Box sx={{ display:'flex', alignItems:'center', gap:'7px', mb:'4px', flexWrap:'wrap' }}>
          <Typography sx={{
            fontSize:'13.5px', fontWeight:700, color:C.text,
            fontFamily:"'DM Sans', sans-serif",
            whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:190,
          }}>
            {hl(lead.name, query)}
          </Typography>

          {lead.project?.name && (
            <Box sx={{ px:'8px', py:'2px', borderRadius:'20px', bgcolor:C.accentSoft, color:C.accent, fontSize:'10.5px', fontWeight:700, fontFamily:"'DM Sans', sans-serif", letterSpacing:'0.04em', whiteSpace:'nowrap', flexShrink:0 }}>
              {lead.project.name}
            </Box>
          )}
          {lead.chanel?.name && (
            <Box sx={{ px:'8px', py:'2px', borderRadius:'20px', bgcolor:'rgba(120,84,247,0.09)', color:'#7854F7', fontSize:'10.5px', fontWeight:700, fontFamily:"'DM Sans', sans-serif", whiteSpace:'nowrap', flexShrink:0 }}>
              {lead.chanel.name}
            </Box>
          )}
        </Box>

        {/* Location pill */}
        {(() => {
          const loc = getLeadLocation(lead);
          return (
            <Box sx={{ display:'inline-flex', alignItems:'center', gap:'4px', px:'7px', py:'2px', borderRadius:'20px', bgcolor:loc.bg, color:loc.color, fontSize:'10px', fontWeight:700, fontFamily:"'DM Sans', sans-serif", whiteSpace:'nowrap', flexShrink:0, mt:'3px' }}>
              {loc.icon}
              {loc.label}
            </Box>
          );
        })()}

        {/* Row 2 — phones */}
        <Box sx={{ display:'flex', alignItems:'center', gap:'14px', flexWrap:'wrap' }}>
          {lead.phone && (
            <Box sx={{ display:'flex', alignItems:'center', gap:'4px' }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="2" strokeLinecap="round">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
              </svg>
              <Typography sx={{ fontSize:'12px', color:C.textMuted, fontFamily:"'DM Sans', sans-serif" }}>
                {hl(lead.phone, query)}
              </Typography>
            </Box>
          )}
          {lead.whatsappnumber && lead.whatsappnumber !== lead.phone && (
            <Box sx={{ display:'flex', alignItems:'center', gap:'4px' }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <Typography sx={{ fontSize:'12px', color:C.textMuted, fontFamily:"'DM Sans', sans-serif" }}>
                {hl(lead.whatsappnumber, query)}
              </Typography>
            </Box>
          )}
          {lead.phonenumber2 && (
            <Box sx={{ display:'flex', alignItems:'center', gap:'4px' }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#B0B7C3" strokeWidth="2" strokeLinecap="round">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
              </svg>
              <Typography sx={{ fontSize:'12px', color:'#B0B7C3', fontFamily:"'DM Sans', sans-serif" }}>
                {hl(lead.phonenumber2, query)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Row 3 — added by */}
        {lead.addby?.name && (
          <Box sx={{ display:'flex', alignItems:'center', gap:'4px', mt:'3px' }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#B0B7C3" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            <Typography sx={{ fontSize:'11px', color:'#B0B7C3', fontFamily:"'DM Sans', sans-serif" }}>
              Added by {lead.addby.name}
            </Typography>
          </Box>
        )}
      </Box>

      {/* CTA */}
      <Box sx={{
        px:'12px', py:'6px', borderRadius:'8px',
        border:`1.5px solid ${C.accent}`,
        color:C.accent, fontSize:'11.5px', fontWeight:700,
        fontFamily:"'DM Sans', sans-serif",
        whiteSpace:'nowrap', flexShrink:0,
        transition:'all 0.13s',
        '&:hover':{ bgcolor:C.accent, color:'#fff' },
      }}>
        Show Details
      </Box>
    </Box>
  );
};

// ─── Main SmartSearch ─────────────────────────────────────────────────────────
export const SmartSearch = ({ role }) => {
  const cfg          = SEARCH_CONFIG[role];
  const isAdmin      = role === 'Admin';
  const isSales      = role === 'Sales';
  const isTeamLeader = role === 'Team Leader';
  // الـ roles اللي بتظهر ليهم الـ panel
  // const hasPanel     = isAdmin || isSales || isTeamLeader;
const hasPanel = isAdmin;
  // ══ ALL HOOKS FIRST — no early return before this block ══════════════════

  const [val,          setVal]          = useState(() => localStorage.getItem(cfg?.storageKey) || '');
  const [focused,      setFocused]      = useState(false);
  const [results,      setResults]      = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [panelOpen,    setPanelOpen]    = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [panelRect,    setPanelRect]    = useState(null);
  const [searched,     setSearched]     = useState(false); // ← عارف إن اليوزر دوس Enter

  const wrapRef     = useRef(null);
  const inputRef    = useRef(null);
  const panelRef    = useRef(null);

  // ── Admin fetch — بيستخدم baseurl اللي فيه interceptor بالـ token ────────
  const fetchResults = useCallback(async (q) => {
    if (!q || !q.trim()) { setResults([]); setPanelOpen(false); return; }
    setLoading(true);
    setSearched(true);
    setPanelOpen(true);
    if (wrapRef.current) setPanelRect(wrapRef.current.getBoundingClientRect());
    try {
      const c = q.trim();
      // نفس getSearchQuery بالظبط
      const searchParam = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c)
        ? `email=${encodeURIComponent(c)}`
        : `keyword=${encodeURIComponent(c)}`;
      const res = await baseurl.get(
        `api/v1/users/admin/allleadspagination?limit=10&page=1&sort=-date&data=false&${searchParam}`
      );
      setResults(res.data?.data || []);
    } catch (e) {
      console.error('[SmartSearch] fetch error:', e);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Close panel on outside click ──────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      const clickedInsideWrap  = wrapRef.current && wrapRef.current.contains(e.target);
      // الـ panel بيتعرض في portal خارج الـ DOM tree — نتحقق بالـ data-attribute
      const clickedInsidePanel = e.target.closest('[data-smartsearch-panel="true"]');
      if (!clickedInsideWrap && !clickedInsidePanel) {
        setFocused(false);
        setPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);


  // ── Update panel rect on scroll / resize ─────────────────────────────────
  useEffect(() => {
    if (!panelOpen) return;
    const update = () => {
      if (wrapRef.current) setPanelRect(wrapRef.current.getBoundingClientRect());
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [panelOpen]);

  // ══ EARLY RETURN — after all hooks ═══════════════════════════════════════
  if (!cfg) return null;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const dispatchGlobal = (v) => {
    localStorage.setItem(cfg.storageKey, v);
    window.dispatchEvent(new Event(cfg.event));
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setVal(v);
    dispatchGlobal(v);
    // لو مسح الكلام → اقفل الـ panel
    if (!v.trim()) {
      setResults([]);
      setPanelOpen(false);
      setSearched(false);
    }
  };

  // ── الأهم: Enter يشغّل الـ fetch للأدمن ──────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && hasPanel) {
      e.preventDefault();
      // ← اهم سطر: خد الـ rect قبل الـ fetch عشان الـ panel يعرف يتعرض فورًا
      if (wrapRef.current) setPanelRect(wrapRef.current.getBoundingClientRect());
      fetchResults(val);
    }
    if (e.key === 'Escape') {
      setPanelOpen(false);
    }
  };

  const handleClear = () => {
    setVal('');
    dispatchGlobal('');
    setResults([]);
    setPanelOpen(false);
    setSearched(false);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleFocus = () => {
    setFocused(true);
    // لو في نتايج قديمة → اعرضها تاني لما يفوكس
    if (hasPanel && results.length > 0) {
      setPanelOpen(true);
      if (wrapRef.current) setPanelRect(wrapRef.current.getBoundingClientRect());
    }
  };

  const handleSelect = (lead) => {
    console.log('[SmartSearch] selected lead:', lead);
    console.log('[SmartSearch] _id:', lead?._id, '| id:', lead?.id);
    setPanelOpen(false);
    setSelectedLead(lead);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Search input */}
      <Box
        ref={wrapRef}
        sx={{
          display:'flex', alignItems:'center',
          flex:1, maxWidth:520,
          px:'12px', height:'38px',
          bgcolor: focused ? '#fff' : '#F9FAFB',
          border:`1px solid ${focused ? C.searchBorderFocus : C.border}`,
          borderRadius:'8px',
          transition:'all 0.18s',
          boxShadow: focused ? `0 0 0 3px ${C.accentSoft}` : 'none',
          gap:'8px',
        }}
      >
        {loading ? (
          <CircularProgress size={15} sx={{ color:C.accent, flexShrink:0 }} />
        ) : (
          <SearchIcon sx={{ fontSize:'17px', color: focused ? C.accent : C.textMuted, flexShrink:0, transition:'color 0.18s' }} />
        )}

        <InputBase
          inputRef={inputRef}
          value={val}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={() => setFocused(false)}
          placeholder={cfg.placeholder}
          sx={{
            flex:1, fontSize:'13.5px',
            fontFamily:"'DM Sans', sans-serif",
            color:C.text,
            '& input::placeholder':{ color:C.textMuted, opacity:1 },
            '& input':{ padding:0 },
          }}
        />

        {/* Enter hint — للأدمن بس */}
        {hasPanel && val && !loading && (
          <Box sx={{
            px:'6px', py:'2px', borderRadius:'5px',
            bgcolor:'#F3F4F6', border:`1px solid ${C.border}`,
            fontSize:'10px', fontWeight:700, color:C.textMuted,
            fontFamily:"'DM Sans', sans-serif",
            flexShrink:0, whiteSpace:'nowrap',
          }}>
            ↵ Enter
          </Box>
        )}

        {/* Clear */}
        {val && (
          <Box
            onClick={handleClear}
            sx={{
              width:18, height:18, borderRadius:'50%',
              bgcolor:'#E5E7EB',
              display:'flex', alignItems:'center', justifyContent:'center',
              cursor:'pointer', flexShrink:0,
              '&:hover':{ bgcolor:'#D1D5DB' },
            }}
          >
            <CloseIcon sx={{ fontSize:'11px', color:C.textMuted }} />
          </Box>
        )}
      </Box>

      {/* ── Admin results panel (portal) ── */}
      {hasPanel && panelOpen && createPortal(
        <Box data-smartsearch-panel="true" sx={{
          position:'fixed',
          top: (panelRect?.bottom ?? 70) + 6,
          left: panelRect?.left ?? 250,
          width: Math.max(panelRect?.width ?? 540, 540),
          zIndex:99999,
          bgcolor:C.bg,
          borderRadius:'14px',
          border:`1px solid ${C.border}`,
          boxShadow:C.shadow,
          overflow:'hidden',
          animation:'srPanelIn 0.2s cubic-bezier(0.34,1.1,0.64,1) both',
          '@keyframes srPanelIn':{
            from:{ opacity:0, transform:'translateY(-6px) scale(0.98)' },
            to:  { opacity:1, transform:'none' },
          },
        }}>

          {/* Panel header */}
          <Box sx={{
            px:'16px', py:'10px',
            borderBottom:`1px solid ${C.border}`,
            bgcolor:'#F9FAFB',
            display:'flex', alignItems:'center', justifyContent:'space-between',
          }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:'8px' }}>
              {loading ? (
                <CircularProgress size={10} sx={{ color:C.accent }} />
              ) : (
                <Box sx={{
                  width:6, height:6, borderRadius:'50%', bgcolor:C.accent,
                  animation:'srPulse 1.8s ease-in-out infinite',
                  '@keyframes srPulse':{ '0%,100%':{ opacity:1 }, '50%':{ opacity:0.3 } },
                }} />
              )}
              <Typography sx={{ fontSize:'11.5px', fontWeight:700, color:C.textMuted, fontFamily:"'DM Sans', sans-serif", letterSpacing:'0.06em', textTransform:'uppercase' }}>
                {loading ? 'Searching...' : `${results.length} result${results.length !== 1 ? 's' : ''} for "${val}"`}
              </Typography>
            </Box>
            <Box
              onClick={() => setPanelOpen(false)}
              sx={{ width:22, height:22, borderRadius:'6px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:C.textMuted, '&:hover':{ bgcolor:C.border, color:C.text } }}
            >
              <CloseIcon sx={{ fontSize:'13px' }} />
            </Box>
          </Box>

          {/* Results list */}
          <Box sx={{
            maxHeight:440, overflowY:'auto',
            scrollbarWidth:'thin', scrollbarColor:`${C.border} transparent`,
            '&::-webkit-scrollbar':{ width:'4px' },
            '&::-webkit-scrollbar-thumb':{ background:C.border, borderRadius:'4px' },
          }}>
            {loading ? (
              /* Loading skeleton */
              [0,1,2].map(i => (
                <Box key={i} sx={{ display:'flex', alignItems:'center', gap:'14px', px:'16px', py:'12px', borderBottom:`1px solid ${C.border}` }}>
                  <Box sx={{ width:42, height:42, borderRadius:'11px', bgcolor:'#F3F4F6', flexShrink:0, animation:'srSkel 1.2s ease-in-out infinite', '@keyframes srSkel':{ '0%,100%':{ opacity:0.6 }, '50%':{ opacity:1 } } }} />
                  <Box sx={{ flex:1 }}>
                    <Box sx={{ height:13, width:'55%', borderRadius:4, bgcolor:'#F3F4F6', mb:'8px' }} />
                    <Box sx={{ height:11, width:'35%', borderRadius:4, bgcolor:'#F3F4F6' }} />
                  </Box>
                </Box>
              ))
            ) : results.length === 0 ? (
              /* Empty state */
              <Box sx={{ py:5, textAlign:'center' }}>
                <Box sx={{ fontSize:36, mb:1, opacity:0.25 }}>🔍</Box>
                <Typography sx={{ fontSize:'13.5px', fontWeight:600, color:'#374151', fontFamily:"'DM Sans', sans-serif", mb:'4px' }}>
                  No leads found
                </Typography>
                <Typography sx={{ fontSize:'12px', color:C.textMuted, fontFamily:"'DM Sans', sans-serif" }}>
                  Try a different name, phone number or email
                </Typography>
              </Box>
            ) : (
              results.map((lead, i) => (
                <ResultRow key={lead._id} lead={lead} query={val} onSelect={handleSelect} index={i} />
              ))
            )}
          </Box>

          {/* Panel footer */}
          {!loading && (
            <Box sx={{
              px:'16px', py:'8px',
              borderTop:`1px solid ${C.border}`,
              bgcolor:'#F9FAFB',
              display:'flex', alignItems:'center', justifyContent:'space-between',
            }}>
              <Box sx={{ display:'flex', alignItems:'center', gap:'6px' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.textMuted} strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <Typography sx={{ fontSize:'11px', color:C.textMuted, fontFamily:"'DM Sans', sans-serif" }}>
                  Click on a result to open full lead details
                </Typography>
              </Box>
              <Typography sx={{ fontSize:'11px', color:C.textMuted, fontFamily:"'DM Sans', sans-serif" }}>
                Press <strong>Esc</strong> to close
              </Typography>
            </Box>
          )}
        </Box>,
        document.body
      )}

      {/* Lead modal */}
      {selectedLead && (selectedLead._id || selectedLead.id) && (
        <ShowClientsActionsAdvanced
          user={{
            ...selectedLead,
            // ضمان وجود _id سواء جه من الـ API بـ _id أو id
            _id: selectedLead._id || selectedLead.id,
          }}
          leadid={selectedLead._id || selectedLead.id}
          onClose={() => setSelectedLead(null)}
        />
      )}
    </>
  );
};

export default SmartSearch;
